"use server";

import { cookies } from "next/headers";
import { revalidatePath } from "next/cache";
import { getImperialSession } from "@/lib/api/session";
import { performLogout } from "@/lib/api/auth-logout";
import { COOKIE_ACTIVE_TAB } from "./types";

/**
 * Server Actions dla SSO session detection (Imperial admin ↔ React-CMS).
 *
 * **Use case:** wykrywanie wylogowania z Imperial. Gdy user wyloguje się
 * w innej karcie (Imperial admin) cookie `IMPERIAL` znika lub session w DB
 * jest unieważniona. React-CMS musi wykryć to zdarzenie żeby zaktualizować
 * UI (ukryć admin features, switch na anonymous).
 *
 * **Mechanizm:**
 *   1. Client Component `<SessionWatcher>` polluje `pingImperialSessionAction()`
 *      co 30s + na `visibilitychange` (focus tab)
 *   2. Server Action wywołuje `getImperialSession()` server-side
 *   3. Gdy session jest aktywna -> `{loggedIn: true, userId, login}`
 *   4. Gdy zniknęła -> `{loggedIn: false}` — client trigger'uje `router.refresh()`
 *      żeby Server Components re-renderowały z anonymous user'em
 *
 * **Per-domain rate limiting** — polling co 30s = 120 req/h per user. OK dla
 * dev, na production rozważyć Server-Sent Events lub WebSocket dla większej
 * skali.
 */

export type SessionPingResult = {
  loggedIn: boolean;
  /** User id — pozwala wykryć "session change" (user przelogował się na inne konto). */
  userId?: number;
  /** Login — diagnostyczne. */
  login?: string;
};

/**
 * Ping Imperial SSO session — odpowiada czy user nadal zalogowany.
 *
 * Wywoływane periodically z Client Component. Cache: no-store (zawsze fresh
 * check, nie cache'ujemy auth state).
 */
export async function pingImperialSessionAction(): Promise<SessionPingResult> {
  const session = await getImperialSession();

  if (!session) {
    return { loggedIn: false };
  }

  return {
    loggedIn: true,
    userId: session.user.id,
    login: session.user.login,
  };
}

/**
 * Force re-render po wykryciu session change (logout / login / user switch).
 *
 * Wywoływane przez Client Component gdy `pingImperialSessionAction` zwróci
 * inny stan niż znany. `revalidatePath('/', 'layout')` wymusza re-fetch w
 * Server Components — PublicLayout ponownie wywoła `getImperialSession()`
 * i zaktualizuje user/scopes/unpublishedWindows.
 */
export async function refreshAfterSessionChangeAction(): Promise<void> {
  revalidatePath("/", "layout");
}

/**
 * Wylogowanie z Imperial SSO (z poziomu React-CMS).
 *
 * **Flow:**
 *   1. Pobierz current SSO session (potrzebny access_token jako Bearer)
 *   2. POST `/IRESTAPI/v1/IIAPI_CONTROLLER/logout` z Bearer -> backend
 *      destroyuje `$_SESSION` (is_logged=0 + clears user data)
 *   3. Usuń cookie `IMPERIAL` lokalnie (na wypadek gdyby backend nie clear'ował;
 *      Imperial używa parent-domain cookie więc reset z `domain` matching)
 *   4. `revalidatePath` -> Server Components re-renderują z anonymous user'em
 *
 * **Side effect SSO:** user jest też wylogowany z Imperial admin — to jest
 * intentional behavior single sign-out (one cookie = one logout = wszędzie).
 *
 * **Idempotentne:** wywołanie gdy user już anonymous -> no-op (silent success).
 */
export async function logoutImperialAction(): Promise<{ ok: boolean; error?: string }> {
  /* Wczesny exit gdy brak sesji — i tak czyścimy cookies poniżej (idempotent). */
  const session = await getImperialSession();
  if (!session) {
    const cookieStore = await cookies();
    cookieStore.delete(COOKIE_ACTIVE_TAB);
    revalidatePath("/", "layout");
    return { ok: true };
  }

  /* Wspólny helper `performLogout` — używany TEŻ przez
     `/api/iiapi/logout`. Single source of truth: forward cookies +
     wywołanie backend `IAPI_logout` + lista cookies do skasowania. */
  const { cookiesToClear } = await performLogout();

  const cookieStore = await cookies();
  for (const name of cookiesToClear) {
    cookieStore.delete(name);
  }
  /* Admin-only: tab state cookie (catalog / layoutAndWindows). */
  cookieStore.delete(COOKIE_ACTIVE_TAB);

  revalidatePath("/", "layout");
  return { ok: true };
}
